import AddIcon from '@mui/icons-material/Add'
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined'
import {
    Box,
    Button,
    IconButton,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import ActiveOrgSwitcher from '@/features/dashboard/site-admin/ActiveOrgSwitcher'
import PageFeedbackButton from '@/features/feedback/PageFeedbackButton'
import DemoTourButton from '@/features/tour/DemoTourButton'
import useAuth from '@/hooks/useAuth'
import { isLocationScoped } from '@/lib/locationScope'
import { dashboardCreateOptions, getDashboardPageTitle } from '@/routeConfig'
import { AccountButton } from '../nav-buttons/account-button'
import { NotificationsButton } from '../nav-buttons/notifications-button'
import { CreateModal } from './CreateModal'
import LocationScopeBadge from './LocationScopeBadge'

const TOP_NAV_HEIGHT = 64

const hasCreateActions = dashboardCreateOptions.length > 0

export function AppTopBar() {
    const { isAuthenticated, user } = useAuth()
    // A location member sees their Location(s) in place of the org switcher, and
    // no create actions (Phase 1 location access is read-only).
    const scoped = isLocationScoped(user)
    const { pathname } = useLocation()
    const pageTitle = getDashboardPageTitle(pathname)
    const smUp = useMediaQuery((theme) => theme.breakpoints.up('sm'))
    const [createOpen, setCreateOpen] = useState(false)

    return (
        <>
            <Box
                component="header"
                sx={(theme) => ({
                    backdropFilter: 'blur(6px)',
                    backgroundColor: alpha(
                        theme.palette.background.default,
                        0.8
                    ),
                    position: 'sticky',
                    top: 0,
                    zIndex: theme.zIndex.appBar,
                    // Notch / status bar (landscape phones, standalone PWA).
                    // 0 everywhere else; needs viewport-fit=cover.
                    paddingTop: 'env(safe-area-inset-top, 0px)'
                })}
            >
                <Stack
                    alignItems="center"
                    direction="row"
                    justifyContent="space-between"
                    sx={{
                        minHeight: TOP_NAV_HEIGHT,
                        // Left inset only on xs: from sm up the sidebar sits at
                        // the left edge and absorbs it (see AppSidebar).
                        pl: {
                            xs: 'max(16px, env(safe-area-inset-left, 0px))',
                            sm: 2
                        },
                        pr: 'max(16px, env(safe-area-inset-right, 0px))',
                        gap: 1
                    }}
                >
                    {/* Left: page title */}
                    <Typography
                        variant="h6"
                        component="h1"
                        noWrap
                        sx={{ minWidth: 0 }}
                    >
                        {/*    {pageTitle ?? ''}*/}
                    </Typography>

                    {/* Right: action buttons */}
                    <Stack
                        alignItems="center"
                        direction="row"
                        spacing={1}
                        sx={{ minWidth: 0 }}
                    >
                        <NotificationsButton />

                        {isAuthenticated ? (
                            <>
                                <DemoTourButton />
                                <PageFeedbackButton />
                                {scoped ? (
                                    <LocationScopeBadge />
                                ) : (
                                    <ActiveOrgSwitcher />
                                )}
                                {hasCreateActions && !scoped && (
                                    <Tooltip title="Create">
                                        <IconButton
                                            onClick={() => setCreateOpen(true)}
                                            aria-label="Open create menu"
                                            size={smUp ? 'large' : 'small'}
                                        >
                                            <AddIcon
                                                fontSize={
                                                    smUp ? 'large' : 'small'
                                                }
                                            />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <AccountButton />
                            </>
                        ) : (
                            <Button
                                component={Link}
                                to="/login"
                                variant="outlined"
                                size="small"
                                startIcon={<LoginOutlinedIcon />}
                            >
                                Sign in
                            </Button>
                        )}
                    </Stack>
                </Stack>
            </Box>

            {isAuthenticated && (
                <CreateModal
                    open={createOpen}
                    onClose={() => setCreateOpen(false)}
                />
            )}
        </>
    )
}
